import "./WaitingRoomPage.css";
import {
  FaArrowRight,
  FaChevronDown,
  FaClock,
  FaMicrophone,
  FaMicrophoneSlash,
  FaTimes,
  FaVideo,
  FaVideoSlash,
  FaVolumeUp,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { fetchAgoraToken } from "../components/Agora/helpers";
import {
  buildVideoSessionDevices,
  clearWaitingRoomSession,
  loadWaitingRoomSession,
  loadVideoSessionDeviceDefaults,
  saveVideoCallSession,
  saveWaitingRoomSession,
  saveVideoSessionDeviceDefaults,
  saveVideoSessionDevices,
} from "../utils/videoSessionDevices";

dayjs.extend(advancedFormat);

const CANCELLED_STATUSES = [
  "canceled_by_user",
  "Canceled by Provider",
  "Canceled by Super Admin",
  "System Cancel",
  "late_cancelled",
  "late_cancel",
  "Cancelled",
];

const isCancelledAppointment = (appointment) =>
  appointment?.isCancelled || CANCELLED_STATUSES.includes(appointment?.status);

const getAppointmentEndTime = (apt) => {
  if (!apt?.appointmenttime) return null;

  if (apt.appointmentEndTime) {
    return new Date(apt.appointmentEndTime);
  }

  const startTime = new Date(apt.appointmenttime);
  const durationMinutes = apt.sessionLength || 30;

  return new Date(startTime.getTime() + durationMinutes * 60000);
};

const hasAppointmentEnded = (appointment) => {
  const endTime = getAppointmentEndTime(appointment);
  if (!endTime || Number.isNaN(endTime.getTime())) return false;

  return new Date() >= endTime;
};

const canStartVideoSession = (appointment) => {
  if (!appointment) return false;
  if (isCancelledAppointment(appointment)) return false;
  if (appointment.status === "Completed" || appointment.status === "No Show") {
    return false;
  }
  if (hasAppointmentEnded(appointment)) return false;

  return true;
};

const getDeviceLabel = (device, fallback) => device?.label?.trim() || fallback;

function DeviceSelectField({
  id,
  label,
  icon,
  value,
  onChange,
  disabled,
  loading,
  emptyLabel,
  devices,
  fallbackLabel,
}) {
  const selected = devices.find((item) => item.deviceId === value);
  const displayLabel = loading
    ? "Loading devices..."
    : devices.length === 0
      ? emptyLabel
      : getDeviceLabel(selected, fallbackLabel);

  return (
    <div className="waiting-room-device-field">
      <label htmlFor={id}>{label}</label>
      <div className="waiting-room-device-select-wrap">
        {icon}
        <span className="waiting-room-device-value" title={displayLabel}>
          {displayLabel}
        </span>
        <FaChevronDown className="waiting-room-device-chevron" />
        <select
          aria-label={label}
          className="waiting-room-device-select"
          disabled={disabled}
          id={id}
          onChange={onChange}
          value={value}
        >
          {loading && <option value={value || ""}>Loading devices...</option>}
          {!loading && devices.length === 0 && (
            <option value="">{emptyLabel}</option>
          )}
          {devices.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {getDeviceLabel(device, fallbackLabel)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

const getLiveTrack = (stream, kind) =>
  stream
    ?.getTracks()
    .find((track) => track.kind === kind && track.readyState === "live");

const releaseTracks = (stream, kind) => {
  if (!stream) return;

  const tracks =
    kind === "video"
      ? [...stream.getVideoTracks()]
      : kind === "audio"
        ? [...stream.getAudioTracks()]
        : [...stream.getTracks()];

  tracks.forEach((track) => {
    track.stop();
    if (stream.getTracks().includes(track)) {
      stream.removeTrack(track);
    }
  });
};

const pruneEndedTracks = (stream) => {
  if (!stream) return;

  [...stream.getTracks()].forEach((track) => {
    if (track.readyState === "ended") {
      stream.removeTrack(track);
    }
  });
};

const buildVideoConstraints = (selectedCameraId) =>
  selectedCameraId ? { deviceId: { ideal: selectedCameraId } } : true;

const buildAudioConstraints = (selectedMicId) =>
  selectedMicId ? { deviceId: { ideal: selectedMicId } } : true;

const WaitingRoomPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const speakerAudioRef = useRef(null);
  const syncGenerationRef = useRef(0);
  const savedDefaultsRef = useRef(loadVideoSessionDeviceDefaults());

  const isCareManager =
    user?.user?.role === "careManager" ||
    user?.user?.providerType === "Care Manager";
  const isTherapistProvider =
    user?.user?.role === "provider" &&
    user?.user?.providerType !== "Care Manager" &&
    user?.user?.providerType !== "Psychiatric Consultant";
  const canAccessWaitingRoom = isCareManager || isTherapistProvider;

  const [appointment, setAppointment] = useState(
    () => location.state?.appointment || loadWaitingRoomSession() || null,
  );

  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [cameraOn, setCameraOn] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [permissionModalMode, setPermissionModalMode] = useState("camera");
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [mediaPermissionsGranted, setMediaPermissionsGranted] = useState({
    camera: false,
    mic: false,
  });
  const [selectedCameraId, setSelectedCameraId] = useState(
    () => savedDefaultsRef.current?.cameraId ?? "",
  );
  const [selectedMicId, setSelectedMicId] = useState(
    () => savedDefaultsRef.current?.micId ?? "",
  );
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(
    () => savedDefaultsRef.current?.speakerId ?? "",
  );
  const [videoInputs, setVideoInputs] = useState([]);

  useEffect(() => {
    if (location.state?.appointment) {
      setAppointment(location.state.appointment);
      saveWaitingRoomSession(location.state.appointment);
    }
  }, [location.state?.appointment]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "WAITING_ROOM_DATA") return;

      const nextAppointment = event.data?.data?.appointment;
      if (nextAppointment) {
        setAppointment(nextAppointment);
        saveWaitingRoomSession(nextAppointment);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!canAccessWaitingRoom) {
      navigate("/appointments", { replace: true });
      return;
    }

    if (!appointment) {
      navigate("/appointments", { replace: true });
      return;
    }

    if (!canStartVideoSession(appointment)) {
      toast.error("This appointment is no longer available for video.");
      clearWaitingRoomSession();
      navigate("/appointments", { replace: true });
    }
  }, [appointment, canAccessWaitingRoom, navigate]);

  const isStartSessionEnabled = canStartVideoSession(appointment);

  const stopPreviewStream = useCallback(() => {
    if (streamRef.current) {
      releaseTracks(streamRef.current);
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsPreviewReady(false);
  }, []);

  useEffect(() => {
    if (!appointment?._id) return;

    setMediaPermissionsGranted({ camera: false, mic: false });
    setCameraOn(false);
    setMicOn(false);
    setPermissionError("");
    stopPreviewStream();
  }, [appointment?._id, stopPreviewStream]);

  const refreshDeviceLists = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return { microphones: [], cameras: [], speakers: [] };
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphones = devices.filter(
      (device) => device.kind === "audioinput",
    );
    const cameras = devices.filter((device) => device.kind === "videoinput");
    const speakers = devices.filter((device) => device.kind === "audiooutput");

    setAudioInputs(microphones);
    setVideoInputs(cameras);
    setAudioOutputs(speakers);

    return { cameras, microphones, speakers };
  }, []);

  const applyDefaultSelections = useCallback((lists) => {
    const { microphones, cameras, speakers } = lists;

    setSelectedMicId((current) => {
      if (
        current &&
        microphones.some((device) => device.deviceId === current)
      ) {
        return current;
      }
      return microphones[0]?.deviceId || "";
    });

    setSelectedCameraId((current) => {
      if (current && cameras.some((device) => device.deviceId === current)) {
        return current;
      }
      return cameras[0]?.deviceId || "";
    });

    setSelectedSpeakerId((current) => {
      if (current && speakers.some((device) => device.deviceId === current)) {
        return current;
      }
      return speakers[0]?.deviceId || "";
    });
  }, []);

  const attachStreamToPreview = useCallback(async () => {
    const stream = streamRef.current;
    const videoEl = videoRef.current;

    if (!videoEl) return;

    const liveVideoTrack = getLiveTrack(stream, "video");

    if (!liveVideoTrack) {
      if (videoEl.srcObject) {
        videoEl.srcObject = null;
      }
      return;
    }

    const currentStream = videoEl.srcObject;
    const currentVideoTrack = currentStream?.getVideoTracks()?.[0];

    if (
      currentStream !== stream ||
      currentVideoTrack?.id !== liveVideoTrack.id
    ) {
      videoEl.srcObject = stream;

      try {
        await videoEl.play();
      } catch {
        // Autoplay can fail while the element is hidden.
      }
    }
  }, []);

  const ensureMediaStream = useCallback(() => {
    if (!streamRef.current) {
      streamRef.current = new MediaStream();
    }

    return streamRef.current;
  }, []);

  const syncPreviewMedia = useCallback(async () => {
    const generation = ++syncGenerationRef.current;

    if (!cameraOn && !micOn) {
      stopPreviewStream();
      return;
    }

    try {
      let stream = streamRef.current;
      let liveVideo = getLiveTrack(stream, "video");
      let liveAudio = getLiveTrack(stream, "audio");

      if (!cameraOn && liveVideo) {
        releaseTracks(stream, "video");
        liveVideo = null;
      }

      if (!micOn && liveAudio) {
        releaseTracks(stream, "audio");
        liveAudio = null;
      }

      const needsVideo =
        cameraOn &&
        (!liveVideo ||
          (selectedCameraId &&
            liveVideo.getSettings().deviceId !== selectedCameraId));

      if (needsVideo) {
        if (liveVideo) {
          releaseTracks(stream, "video");
        }

        const videoStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: buildVideoConstraints(selectedCameraId),
        });

        if (generation !== syncGenerationRef.current) {
          releaseTracks(videoStream);
          return;
        }

        stream = ensureMediaStream();
        videoStream.getVideoTracks().forEach((track) => {
          stream.addTrack(track);
        });
        liveVideo = videoStream.getVideoTracks()[0];
      }

      const needsAudio =
        micOn &&
        (!liveAudio ||
          (selectedMicId &&
            liveAudio.getSettings().deviceId !== selectedMicId));

      if (needsAudio) {
        if (liveAudio) {
          releaseTracks(stream, "audio");
        }

        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: buildAudioConstraints(selectedMicId),
          video: false,
        });

        if (generation !== syncGenerationRef.current) {
          releaseTracks(audioStream);
          return;
        }

        stream = ensureMediaStream();
        audioStream.getAudioTracks().forEach((track) => {
          stream.addTrack(track);
        });
        liveAudio = audioStream.getAudioTracks()[0];
      }

      if (stream && stream.getTracks().length === 0) {
        streamRef.current = null;
        stream = null;
      } else if (stream) {
        pruneEndedTracks(stream);
      }

      await attachStreamToPreview();

      const hasLiveVideo = Boolean(getLiveTrack(streamRef.current, "video"));
      const hasLiveAudio = Boolean(getLiveTrack(streamRef.current, "audio"));
      setPermissionError("");
      setIsPreviewReady(cameraOn && hasLiveVideo);

      if (hasLiveVideo || hasLiveAudio) {
        setMediaPermissionsGranted((current) => ({
          camera: current.camera || hasLiveVideo,
          mic: current.mic || hasLiveAudio,
        }));
      }

      if (generation !== syncGenerationRef.current) return;

      const lists = await refreshDeviceLists();
      if (generation !== syncGenerationRef.current) return;

      applyDefaultSelections(lists);
    } catch (error) {
      if (generation !== syncGenerationRef.current) return;

      console.error("Waiting room preview error:", error);

      if (cameraOn && !getLiveTrack(streamRef.current, "video")) {
        setCameraOn(false);
        setMediaPermissionsGranted((current) => ({
          ...current,
          camera: false,
        }));
      }

      if (micOn && !getLiveTrack(streamRef.current, "audio")) {
        setMicOn(false);
        setMediaPermissionsGranted((current) => ({ ...current, mic: false }));
      }

      if (
        !streamRef.current
          ?.getTracks()
          .some((track) => track.readyState === "live")
      ) {
        stopPreviewStream();
        setMediaPermissionsGranted({ camera: false, mic: false });
      }
      setPermissionError(
        "Unable to access camera or microphone. Please check your device permissions.",
      );
      setIsPreviewReady(false);
    }
  }, [
    applyDefaultSelections,
    attachStreamToPreview,
    cameraOn,
    ensureMediaStream,
    micOn,
    refreshDeviceLists,
    selectedCameraId,
    selectedMicId,
    stopPreviewStream,
  ]);

  const devicePreferences = useMemo(
    () =>
      buildVideoSessionDevices({
        cameraId: selectedCameraId,
        cameraOn,
        micId: selectedMicId,
        micOn,
        speakerId: selectedSpeakerId,
      }),
    [cameraOn, micOn, selectedCameraId, selectedMicId, selectedSpeakerId],
  );

  useEffect(() => {
    saveVideoSessionDeviceDefaults(devicePreferences);
  }, [devicePreferences]);

  useEffect(() => {
    if (!canAccessWaitingRoom || !appointment) return undefined;

    let isMounted = true;

    const initializeDevices = async () => {
      setDevicesLoading(true);

      try {
        const lists = await refreshDeviceLists();
        if (isMounted) {
          applyDefaultSelections(lists);
        }
      } catch (error) {
        console.error("Waiting room device init error:", error);
      } finally {
        if (isMounted) {
          setDevicesLoading(false);
        }
      }
    };

    initializeDevices();

    return () => {
      isMounted = false;
      stopPreviewStream();
    };
  }, [
    appointment,
    applyDefaultSelections,
    canAccessWaitingRoom,
    refreshDeviceLists,
    stopPreviewStream,
  ]);

  useEffect(() => {
    if (!cameraOn || !isPreviewReady || !videoRef.current) return;

    const videoEl = videoRef.current;

    videoEl.play().catch(() => {
      // Ignore autoplay errors after toggling preview on.
    });
  }, [cameraOn, isPreviewReady]);

  useEffect(() => {
    if (!canAccessWaitingRoom || !appointment || devicesLoading)
      return undefined;

    syncPreviewMedia();

    return () => {
      syncGenerationRef.current += 1;
    };
  }, [
    appointment,
    cameraOn,
    canAccessWaitingRoom,
    devicesLoading,
    micOn,
    selectedCameraId,
    selectedMicId,
    syncPreviewMedia,
  ]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return undefined;

    const handleDeviceChange = async () => {
      const lists = await refreshDeviceLists();
      applyDefaultSelections(lists);
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [applyDefaultSelections, refreshDeviceLists]);

  useEffect(() => {
    const audioElement = speakerAudioRef.current;
    if (
      !audioElement ||
      !selectedSpeakerId ||
      typeof audioElement.setSinkId !== "function"
    ) {
      return;
    }

    audioElement.setSinkId(selectedSpeakerId).catch((error) => {
      console.warn("Unable to set speaker output:", error);
    });
  }, [selectedSpeakerId]);

  useEffect(
    () => () => {
      stopPreviewStream();
    },
    [stopPreviewStream],
  );

  const patientName = `${appointment?.user?.firstname ?? ""} ${
    appointment?.user?.lastname ?? ""
  }`.trim();

  const appointmentSummary = useMemo(() => {
    if (!appointment?.appointmenttime) return "";

    const start = dayjs(appointment.appointmenttime);
    const duration = appointment.sessionLength || 30;

    return `${start.format("MMMM Do")}, ${start.format("h:mm A")} (${duration} min)`;
  }, [appointment]);

  const openPermissionModal = useCallback((mode) => {
    setPermissionModalMode(mode);
    setPermissionModalOpen(true);
  }, []);

  const closePermissionModal = useCallback(() => {
    setPermissionModalOpen(false);
  }, []);

  const applyMediaPermissionChoice = useCallback(
    async ({ enableCamera, enableMic, requestCamera, requestMic }) => {
      try {
        setPermissionError("");
        setPermissionModalOpen(false);

        const constraints = {
          audio: requestMic ? buildAudioConstraints(selectedMicId) : false,
          video: requestCamera
            ? buildVideoConstraints(selectedCameraId)
            : false,
        };

        stopPreviewStream();

        const mediaStream =
          await navigator.mediaDevices.getUserMedia(constraints);
        const videoTrack = mediaStream.getVideoTracks()[0];
        const audioTrack = mediaStream.getAudioTracks()[0];
        const finalStream = new MediaStream();

        if (enableCamera && videoTrack) {
          finalStream.addTrack(videoTrack);
        } else if (videoTrack) {
          videoTrack.stop();
        }

        if (enableMic && audioTrack) {
          finalStream.addTrack(audioTrack);
        } else if (audioTrack) {
          audioTrack.stop();
        }

        streamRef.current =
          finalStream.getTracks().length > 0 ? finalStream : null;

        setMediaPermissionsGranted({
          camera: requestCamera,
          mic: requestMic,
        });
        setCameraOn(enableCamera);
        setMicOn(enableMic);

        await attachStreamToPreview();
        setIsPreviewReady(
          enableCamera && Boolean(getLiveTrack(finalStream, "video")),
        );

        const lists = await refreshDeviceLists();
        applyDefaultSelections(lists);
      } catch (error) {
        console.error("Waiting room permission error:", error);
        stopPreviewStream();
        setMediaPermissionsGranted({ camera: false, mic: false });
        setCameraOn(false);
        setMicOn(false);
        setIsPreviewReady(false);
        setPermissionError(
          "Unable to access camera or microphone. Please check your device permissions.",
        );
        setPermissionModalOpen(true);
      }
    },
    [
      applyDefaultSelections,
      attachStreamToPreview,
      refreshDeviceLists,
      selectedCameraId,
      selectedMicId,
      stopPreviewStream,
    ],
  );

  const enableCameraOnly = useCallback(() => {
    applyMediaPermissionChoice({
      enableCamera: true,
      enableMic: false,
      requestCamera: true,
      requestMic: false,
    });
  }, [applyMediaPermissionChoice]);

  const enableCameraAndMic = useCallback(() => {
    applyMediaPermissionChoice({
      enableCamera: true,
      enableMic: true,
      requestCamera: true,
      requestMic: true,
    });
  }, [applyMediaPermissionChoice]);

  const enableMicOnly = useCallback(() => {
    applyMediaPermissionChoice({
      enableCamera: false,
      enableMic: true,
      requestCamera: false,
      requestMic: true,
    });
  }, [applyMediaPermissionChoice]);

  const enableMicAndCamera = useCallback(() => {
    applyMediaPermissionChoice({
      enableCamera: true,
      enableMic: true,
      requestCamera: true,
      requestMic: true,
    });
  }, [applyMediaPermissionChoice]);

  const openVideoCall = async (credentials) => {
    const preferences = buildVideoSessionDevices({
      cameraId: selectedCameraId,
      cameraOn,
      micId: selectedMicId,
      micOn,
      speakerId: selectedSpeakerId,
    });

    stopPreviewStream();
    setCameraOn(false);
    setMicOn(false);
    setMediaPermissionsGranted({ camera: false, mic: false });
    saveVideoSessionDevices(preferences);

    // Let the browser release the waiting-room camera before Agora opens it.
    await new Promise((resolve) => setTimeout(resolve, 500));

    const payload = {
      agoraCredentials: credentials,
      appointment,
      authToken: user.user?.token,
      devicePreferences: preferences,
      isCareManager,
      provider: user.user,
      user: appointment.user,
    };

    // Care Manager: open video session in the same waiting-room tab
    // so schedule tab stays available and no extra tab is created.
    saveVideoCallSession(payload);
    clearWaitingRoomSession();
    navigate("/video-call", { replace: true, state: payload });
  };

  const handleStartSession = async () => {
    if (!isStartSessionEnabled) {
      toast.error("This appointment is no longer available for video.");
      return;
    }

    // Always ask the backend: credentials cached on the appointment row can hold an
    // expired Agora token, which silently keeps the provider out of the client's channel.
    let credentials;
    setIsJoining(true);

    try {
      const authToken = user.user?.token;
      const result = await fetchAgoraToken(appointment._id, authToken);

      if (result.success) {
        credentials = result.data;
      } else {
        toast.error(`Failed to setup video call: ${result.error}`);
        return;
      }
    } catch (error) {
      console.error("WaitingRoom join error:", error);
      toast.error("Failed to setup video call. Please try again.");
      return;
    } finally {
      setIsJoining(false);
    }

    await openVideoCall(credentials);
  };

  const toggleMic = () => {
    setPermissionError("");

    if (micOn) {
      setMicOn(false);
      return;
    }

    if (mediaPermissionsGranted.mic) {
      setMicOn(true);
      return;
    }

    openPermissionModal("mic");
  };

  const toggleCamera = () => {
    setPermissionError("");

    if (cameraOn) {
      setCameraOn(false);
      return;
    }

    if (mediaPermissionsGranted.camera) {
      setCameraOn(true);
      return;
    }

    openPermissionModal("camera");
  };

  const permissionModalCopy =
    permissionModalMode === "mic"
      ? {
          primaryAction: enableMicOnly,
          primaryLabel: "Use microphone",
          secondaryAction: enableMicAndCamera,
          secondaryLabel: "Use microphone and camera",
          subtitle:
            "You can still turn off your camera or microphone at any time during the meeting.",
          title: "Do you want people to hear you in the meeting?",
        }
      : {
          primaryAction: enableCameraOnly,
          primaryLabel: "Use camera",
          secondaryAction: enableCameraAndMic,
          secondaryLabel: "Use microphone and camera",
          subtitle:
            "You can still turn off your camera or microphone at any time during the meeting.",
          title: "Do you want people to see you in the meeting?",
        };

  if (!canAccessWaitingRoom || !appointment) {
    return null;
  }

  return (
    <div className="waiting-room-page">
      <audio aria-hidden="true" ref={speakerAudioRef} />

      {permissionModalOpen && (
        <div
          aria-labelledby="waiting-room-permission-title"
          aria-modal="true"
          className="waiting-room-permission-overlay"
          role="dialog"
        >
          <div className="waiting-room-permission-modal">
            <button
              aria-label="Close"
              className="waiting-room-permission-close"
              onClick={closePermissionModal}
              type="button"
            >
              <FaTimes />
            </button>

            <div className="waiting-room-permission-illustration">
              <span className="waiting-room-permission-icon is-camera">
                <FaVideo />
              </span>
              <span className="waiting-room-permission-icon is-mic">
                <FaMicrophone />
              </span>
            </div>

            <h2
              className="waiting-room-permission-title"
              id="waiting-room-permission-title"
            >
              {permissionModalCopy.title}
            </h2>
            <p className="waiting-room-permission-subtitle">
              {permissionModalCopy.subtitle}
            </p>

            <div className="waiting-room-permission-actions">
              <button
                className="waiting-room-permission-btn is-primary"
                onClick={permissionModalCopy.primaryAction}
                type="button"
              >
                {permissionModalMode === "camera" ? (
                  <FaVideo />
                ) : (
                  <FaMicrophone />
                )}
                {permissionModalCopy.primaryLabel}
              </button>

              <button
                className="waiting-room-permission-btn is-secondary"
                onClick={permissionModalCopy.secondaryAction}
                type="button"
              >
                <FaVideo />
                <FaMicrophone />
                {permissionModalCopy.secondaryLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="waiting-room-shell">
        <section className="waiting-room-preview-panel">
          <p className="waiting-room-preview-caption">
            Here&apos;s how you&apos;ll appear in the session.
          </p>

          <div className="waiting-room-preview-frame">
            <video
              autoPlay
              className={`waiting-room-preview-video${
                cameraOn && isPreviewReady ? " is-visible" : ""
              }`}
              muted
              playsInline
              ref={videoRef}
            />

            <div
              className={`waiting-room-preview-placeholder${
                cameraOn && isPreviewReady ? " is-hidden" : ""
              }`}
            >
              <FaVideo />
              <span>
                {permissionError ||
                  (devicesLoading ? "Loading devices..." : "Camera is off")}
              </span>
            </div>

            <span
              className={`waiting-room-camera-badge${
                cameraOn && isPreviewReady ? " is-on" : ""
              }`}
            >
              <span className="waiting-room-camera-badge-dot" />
              {cameraOn && isPreviewReady ? "CAMERA ON" : "CAMERA OFF"}
            </span>

            <div className="waiting-room-preview-controls">
              <button
                aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
                aria-pressed={micOn}
                className={`waiting-room-control-btn${
                  micOn ? " is-active" : " is-muted"
                }`}
                onClick={toggleMic}
                type="button"
              >
                {micOn ? <FaMicrophone /> : <FaMicrophoneSlash />}
              </button>

              <button
                aria-label={cameraOn ? "Turn camera off" : "Turn camera on"}
                aria-pressed={cameraOn}
                className={`waiting-room-control-btn${
                  cameraOn ? " is-active" : " is-muted"
                }`}
                onClick={toggleCamera}
                type="button"
              >
                {cameraOn ? <FaVideo /> : <FaVideoSlash />}
              </button>
            </div>
          </div>
        </section>

        <aside className="waiting-room-sidebar">
          <section className="waiting-room-sidebar-section">
            <h2 className="waiting-room-section-label">Patient Details</h2>
            <p className="waiting-room-patient-name">
              {patientName || "Unknown Patient"}
            </p>
            <p className="waiting-room-appointment-time">
              <FaClock className="waiting-room-time-icon" />
              {appointmentSummary}
            </p>
          </section>

          <section className="waiting-room-sidebar-section">
            <h2 className="waiting-room-section-label">Device Settings</h2>

            <DeviceSelectField
              devices={audioInputs}
              disabled={devicesLoading || audioInputs.length === 0}
              emptyLabel="No microphone found"
              fallbackLabel="Microphone"
              icon={<FaMicrophone className="waiting-room-device-icon" />}
              id="waiting-room-microphone"
              label="Microphone"
              loading={devicesLoading}
              onChange={(event) => setSelectedMicId(event.target.value)}
              value={selectedMicId}
            />

            <DeviceSelectField
              devices={videoInputs}
              disabled={devicesLoading || videoInputs.length === 0}
              emptyLabel="No camera found"
              fallbackLabel="Camera"
              icon={<FaVideo className="waiting-room-device-icon" />}
              id="waiting-room-camera"
              label="Camera"
              loading={devicesLoading}
              onChange={(event) => setSelectedCameraId(event.target.value)}
              value={selectedCameraId}
            />

            <DeviceSelectField
              devices={audioOutputs}
              disabled={devicesLoading || audioOutputs.length === 0}
              emptyLabel="Default speaker"
              fallbackLabel="Speaker"
              icon={<FaVolumeUp className="waiting-room-device-icon" />}
              id="waiting-room-speaker"
              label="Speaker"
              loading={devicesLoading}
              onChange={(event) => setSelectedSpeakerId(event.target.value)}
              value={selectedSpeakerId}
            />
          </section>

          <button
            className="waiting-room-start-btn"
            disabled={isJoining || devicesLoading || !isStartSessionEnabled}
            onClick={handleStartSession}
            type="button"
          >
            {isJoining ? "Starting..." : "Start Session"}
            {!isJoining && <FaArrowRight />}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default WaitingRoomPage;
